// 09-10 (maqueta aprobada «el aviso del móvil abre el agente»): tocar la notificación de un agente abre SU chat
// en la oficina. El service worker (public/sw.js, `notificationclick`) enfoca la app si ya está abierta y le manda
// OPEN_AGENT; aquí se escucha en toda la app autenticada (también desde /home) y se navega a
// /oficina?agente=<id>. Se contesta por el puerto del mensaje: si nadie contesta (una versión vieja de la app
// abierta), el SW navega la ventana él mismo. Con la app cerrada, el SW la abre ya en esa dirección.
// 04-11 (Pau: «no recibo notificaciones … cuando santi me escribe»): un aviso puede traer su dirección (`url` del
// payload, p. ej. /oficina?santi=12): el SW manda OPEN_URL y aquí se navega a ella, igual que con un agente. Lo mismo
// para la campana y el toast de un aviso con `url` (lib/abrir-en-la-app.ts): misma navegación que tocar la notificación.
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { escucharAbrirUrl } from '../../lib/abrir-en-la-app';

export const MENSAJE_ABRIR_AGENTE = 'OPEN_AGENT';
export const MENSAJE_ABRIR_URL = 'OPEN_URL';
export const MENSAJE_ABIERTO = 'OPEN_AGENT_OK';

/** Las ventanas que se abren encima de la oficina por la dirección: una sola a la vez (abrir una quita las otras). */
const VENTANAS = ['agente', 'santi', 'sala'] as const;

/**
 * A dónde ir para ver el chat de `agentId`. Si ya se está en la oficina, se conservan sus parámetros (p. ej.
 * `?v2=1`) salvo las otras ventanas (la sala, la de Santi), que taparían el chat; desde cualquier otra pantalla, la
 * oficina limpia.
 */
export function rutaDelAgente(agentId: string, actual: { pathname: string; search: string }): string {
  const params = new URLSearchParams(actual.pathname === '/oficina' ? actual.search : '');
  for (const v of VENTANAS) params.delete(v);
  params.set('agente', agentId);
  return `/oficina?${params.toString()}`;
}

/**
 * A dónde ir para un aviso que trae su dirección. Solo direcciones de esta web (mismo origen; si no, null), sin el
 * prefijo del despliegue (`base`, el basename del router). Ya en la oficina, se conservan sus parámetros (p. ej.
 * `?v2=1`) salvo las otras ventanas, que taparían la que se abre.
 */
export function rutaDelUrl(url: string, actual: { pathname: string; search: string }, entorno: { origen: string; base: string }): string | null {
  let u: URL;
  try {
    u = new URL(url, entorno.origen);
  } catch {
    return null;
  }
  if (u.origin !== entorno.origen) return null;
  const base = entorno.base.replace(/\/+$/, '');
  const ruta = base && (u.pathname === base || u.pathname.startsWith(`${base}/`)) ? u.pathname.slice(base.length) || '/' : u.pathname;
  if (ruta !== '/oficina' || actual.pathname !== '/oficina') return `${ruta}${u.search}${u.hash}`;
  const params = new URLSearchParams(actual.search);
  for (const v of VENTANAS) params.delete(v);
  new URLSearchParams(u.search).forEach((valor, clave) => params.set(clave, valor));
  const q = params.toString();
  return `/oficina${q ? `?${q}` : ''}${u.hash}`;
}

/** El mensaje del SW, si es «abre esta dirección»: la dirección; si no, null. */
export function urlDelMensaje(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const { type, url } = data as { type?: unknown; url?: unknown };
  return type === MENSAJE_ABRIR_URL && typeof url === 'string' && url ? url : null;
}

/** El mensaje del SW, si es «abre este agente»: su id; si no, null. */
export function agenteDelMensaje(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null;
  const { type, agentId } = data as { type?: unknown; agentId?: unknown };
  return type === MENSAJE_ABRIR_AGENTE && typeof agentId === 'string' && agentId ? agentId : null;
}

export function useAbrirDesdeAviso(): void {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  // La campana y el toast (abrirUrlEnLaApp): la misma ruta que el toque en la notificación.
  useEffect(() => {
    const dejarDeEscuchar = escucharAbrirUrl((url) => {
      const destino = rutaDelUrl(url, { pathname, search }, { origen: window.location.origin, base: import.meta.env.BASE_URL ?? '/' });
      if (!destino) return false;
      navigate(destino);
      return true;
    });
    return dejarDeEscuchar;
  }, [navigate, pathname, search]);
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !navigator.serviceWorker) return;
    const sw = navigator.serviceWorker;
    const alMensaje = (e: MessageEvent) => {
      const agentId = agenteDelMensaje(e.data);
      const url = agentId ? null : urlDelMensaje(e.data);
      const destino = agentId
        ? rutaDelAgente(agentId, { pathname, search })
        : url
          ? rutaDelUrl(url, { pathname, search }, { origen: window.location.origin, base: import.meta.env.BASE_URL ?? '/' })
          : null;
      // Sin destino no se contesta: el SW navega la ventana él mismo (o no era para nosotros).
      if (!destino) return;
      e.ports[0]?.postMessage({ type: MENSAJE_ABIERTO });
      navigate(destino);
    };
    sw.addEventListener('message', alMensaje);
    return () => sw.removeEventListener('message', alMensaje);
  }, [navigate, pathname, search]);
}
